import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import { renderToStaticMarkup } from 'react-dom/server';
import moment from 'moment/moment';
import Title from '../../../../commonComponents/Title';
import ContentContainer from '../../../../commonComponents/pageLayout/ContentContainer';
import SubHeader from '../../../../commonComponents/pageLayout/SubHeader';
import TopicBanner from '../../../../commonComponents/pageLayout/header/TopicBanner';
import ReportTabBtns from '../../tab/ReportTabBtns';
import { REPORT_TAB_TYPE } from '../../propTypes/reportTabProp';
import translate from '../../../../commonComponents/translate';
import ServerSideList from '../../../../commonComponents/lists/ServerSideList';
import UserColumn from '../../../../commonComponents/lists/columns/UserColumn';
import ReportFilters from './ReportFilters';
import CountryProp from '../../../../commonComponents/forms/Address/CountryProp';
import { useModal } from '../../../../services/customHooks';
import DetailModal from './DetailModal';

const concatenateFilters = (baseString, filters) => {
  const filterParams = [];

  for (const key in filters) {
    if (filters[key] !== null) {
      filterParams.push(`${key}=${filters[key]}`);
    }
  }

  if (filterParams.length > 0) {
    const filterString = filterParams.join('&');
    return `${baseString}${filterString}`;
  }
  return baseString;
};

const renderAccessBadges = access =>
  renderToStaticMarkup(
    <div>
      {access?.length > 0
        ? access.map((a, i) => (
            <div
              key={a.name}
              style={{
                backgroundColor: a.color,
                marginBottom: i === 0 ? 0 : '5px',
              }}
              className="badge-access"
            >
              {a.name}
            </div>
          ))
        : ' - '}
    </div>,
  );

function TeamReport(props) {
  const { t, countries } = props;
  const tableUrL = '/table/tailor-made/neofin/user-reports';
  const tableId = 'neofin_tailor_made';

  const [filters, setFilters] = useState({
    search: null,
    createDateStart: null,
    createDateEnd: null,
    shippingCountry: null,
    businessLicense: null,
    access: null,
    active: null,
    forecastLevel: null,
    level: null,
    sponsoredBy: null,
    referral: null,
  });
  const [detailModal, setDetailModal] = useModal({ show: false, data: null });

  const handleDetailModal = useCallback(
    (show, detail) => setDetailModal({ show, data: detail }),
    [setDetailModal],
  );

  const handleFiltersChange = useCallback(
    field => value =>
      setFilters(prevState => ({ ...prevState, [field]: value })),
    [setFilters],
  );
  const handleDateChange = (startDate, endDate) => {
    setFilters(prevState => ({
      ...prevState,
      createDateStart: startDate,
      createDateEnd: endDate,
    }));
  };

  const handleExportList = useCallback(() => {
    const endpoint = concatenateFilters(
      '/excel/tailor-made/neofin/user-reports/xlsx?',
      filters,
    );
    window.open(endpoint);
  }, [filters]);

  const columns = [
    {
      field: 'createdAt',
      textAlign: 'left',
      title: t('created.at'),
      width: 100,
      sortable: 'desc',
      template: ({ createdAt }) =>
        renderToStaticMarkup(<>{moment(createdAt).format('DD MMM YYYY')}</>),
    },
    {
      field: 'firstName',
      textAlign: 'left',
      title: t('profile_firstName'),
      width: 100,
      sortable: true,
      template: ({ firstName, lastName, email }) =>
        renderToStaticMarkup(
          <div className="name">
            {!firstName && !lastName ? email : firstName || ' - '}
          </div>,
        ),
    },
    {
      field: 'lastName',
      textAlign: 'left',
      title: t('profile_lastName'),
      width: 100,
      sortable: true,
      template: ({ lastName }) =>
        renderToStaticMarkup(<div className="name">{lastName || ' - '}</div>),
    },
    {
      field: 'shippingCountry',
      textAlign: 'left',
      title: t('address_countryCode'),
      width: 80,
      sortable: true,
      template: ({ shippingCountryInfo }) =>
        renderToStaticMarkup(
          shippingCountryInfo?.code ? (
            <img
              className="countryInfo"
              src={shippingCountryInfo?.flag?.medium}
              alt={shippingCountryInfo?.code}
            />
          ) : (
            ' - '
          ),
        ),
    },
    {
      field: 'businessLicense',
      textAlign: 'center',
      title: t('report.neofin.businessLicense'),
      width: 120,
      sortable: false,
      template: ({ businessLicense }) =>
        renderToStaticMarkup(
          <span className={businessLicense ? 'color-dynamic' : 'color-danger'}>
            {businessLicense ? t('yes') : t('no')}
          </span>,
        ),
    },
    {
      field: 'access',
      textAlign: 'center',
      title: t('access'),
      width: 100,
      sortable: false,
      template: ({ access }) => renderAccessBadges(access),
    },
    {
      field: 'isActive',
      textAlign: 'center',
      title: t('active'),
      width: 80,
      sortable: false,
      template: ({ isActive }) =>
        renderToStaticMarkup(
          <span className={isActive ? 'color-dynamic' : 'color-danger'}>
            {isActive ? t('yes') : t('no')}
          </span>,
        ),
    },
    {
      field: 'forecastLevelInfo',
      textAlign: 'center',
      title: t('report.neofin.currentLevel'),
      width: 150,
      sortable: false,
      template: ({ forecastLevelInfo }) =>
        renderToStaticMarkup(
          <p className="level-info">{forecastLevelInfo?.name || ' - '}</p>,
        ),
    },
    {
      field: 'levelInfo',
      textAlign: 'center',
      title: t('report.neofin.levelMax'),
      width: 150,
      sortable: false,
      template: ({ levelInfo }) =>
        renderToStaticMarkup(
          <p className="level-info">{levelInfo?.name || ' - '}</p>,
        ),
    },
    {
      field: 'sponsoredBy',
      textAlign: 'left',
      title: t('sponsor'),
      width: 150,
      sortable: false,
      template: ({ sponsoredBy }) =>
        renderToStaticMarkup(
          sponsoredBy ? (
            <UserColumn t={t} user={sponsoredBy} includeWrapper />
          ) : (
            ' - '
          ),
        ),
    },
    {
      field: 'referral',
      textAlign: 'left',
      title: t('referral'),
      width: 150,
      sortable: false,
      template: ({ referral }) =>
        renderToStaticMarkup(
          referral ? (
            <UserColumn t={t} user={referral} includeWrapper />
          ) : (
            ' - '
          ),
        ),
    },
  ];
  return (
    <>
      {detailModal.show && detailModal.data ? (
        <DetailModal
          user={detailModal.data}
          onClose={() => handleDetailModal(false, null)}
        />
      ) : null}
      <Title title={t('myReports')}>
        <ContentContainer>
          <SubHeader title={t('myReports')} />
          <TopicBanner />
          <div className="m-content" id="neofin-team-report">
            <div className="full-page-container-with-subheader">
              <div id="">
                <div className="row">
                  <div className="col-lg-12">
                    <div className="m-portlet m-portlet--mobile">
                      <ReportTabBtns
                        tabActive={REPORT_TAB_TYPE.NEOFIN_TEAM_REPORT}
                      />
                      <ReportFilters
                        t={t}
                        onDateChange={handleDateChange}
                        onFilterChange={handleFiltersChange}
                        filters={filters}
                        countries={countries}
                      />
                      <div className="export-list">
                        <button
                          type="button"
                          onClick={() => handleExportList()}
                          className="btn btn-excel-export btn-sm"
                        >
                          {t('export.excel')}
                        </button>
                      </div>
                      <div className="list-wrapper">
                        <ServerSideList
                          dataSourceURL={tableUrL}
                          columns={columns}
                          query={filters}
                          id={tableId}
                          onRowClick={detail => handleDetailModal(true, detail)}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </ContentContainer>
      </Title>
    </>
  );
}

TeamReport.propTypes = {
  t: PropTypes.func.isRequired,
  countries: PropTypes.arrayOf(PropTypes.shape(CountryProp.propType)),
};
TeamReport.defaultProps = {
  countries: [],
};

export default translate(TeamReport);
